import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { GlassCard } from "@/components/glass/GlassCard";
import { ArrowLeft, ArrowRight, Check, Buildings, MapPin, UserCircle, Package, Sparkle, Copy } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";

const STEPS = [
  { id: 0, label: "Tenant", icon: Buildings },
  { id: 1, label: "Industry & regions", icon: MapPin },
  { id: 2, label: "First admin", icon: UserCircle },
  { id: 3, label: "Starter pack", icon: Package },
  { id: 4, label: "Confirm", icon: Sparkle },
];

function randomPassword() {
  const c = "abcdefghjkmnpqrstuvwxyz23456789";
  let s = "";
  for (let i = 0; i < 12; i++) s += c.charAt(Math.floor(Math.random() * c.length));
  return s + "!";
}

export default function NewTenantWizard({ open, onOpenChange, onCreated }) {
  const [step, setStep] = useState(0);
  const [industries, setIndustries] = useState([]);
  const [regionsCatalog, setRegionsCatalog] = useState([]);
  const [packs, setPacks] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);

  const [form, setForm] = useState({
    name: "",
    slug: "",
    industry: "general",
    regions: ["global"],
    admin_email: "",
    admin_name: "",
    admin_password: randomPassword(),
    starter_pack_id: "",
  });

  useEffect(() => {
    if (!open) return;
    api.get("/compliance/frameworks").then(({ data }) => {
      setIndustries(data.industries || []);
      setRegionsCatalog(data.regions || []);
    }).catch(() => {});
    api.get("/starter-packs").then(({ data }) => setPacks(data.packs || [])).catch(() => setPacks([]));
    setStep(0);
    setCreated(null);
    setForm({
      name: "", slug: "", industry: "general", regions: ["global"],
      admin_email: "", admin_name: "", admin_password: randomPassword(), starter_pack_id: "",
      starter_pack_slices: ["presets", "workflows", "boilerplates", "mappings"],
    });
  }, [open]);

  const slugify = (v) => v.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  const canNext = () => {
    if (step === 0) return form.name.trim().length >= 2 && form.slug.replace(/-/g, "").length > 0;
    if (step === 1) return form.regions.length > 0;
    if (step === 2) return /\S+@\S+\.\S+/.test(form.admin_email) && form.admin_password.length >= 8;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/global/tenants", form);
      setCreated(data);
      setStep(5); // completion screen
      toast.success(`Tenant "${form.name}" provisioned`);
      onCreated?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create tenant");
    } finally { setSubmitting(false); }
  };

  const copyCreds = () => {
    const text = `Tenant: ${form.name}\nSlug: ${form.slug}\nAdmin email: ${form.admin_email}\nTemp password: ${form.admin_password}\nLogin: https://clariva.ai/login`;
    navigator.clipboard?.writeText(text).then(() => toast.success("Credentials copied"));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="new-tenant-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Buildings size={18} weight="duotone" className="text-[var(--accent)]" />
            Provision a new tenant
          </DialogTitle>
          <DialogDescription>Create the organisation, its first admin, and optionally seed it with an industry starter pack.</DialogDescription>
        </DialogHeader>

        <div className="flex gap-1 mb-3">
          {STEPS.map((s) => (
            <div key={s.id} className={`flex-1 h-1 rounded-full ${step >= s.id ? "bg-[var(--accent)]" : "bg-[var(--border)]"}`} />
          ))}
        </div>

        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div key="s0" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-3">
              <div>
                <Label className="text-xs">Tenant name</Label>
                <Input
                  value={form.name}
                  autoFocus
                  onChange={(e) => {
                    const n = e.target.value;
                    setForm((f) => ({ ...f, name: n, slug: f.slug && f.slug !== slugify(f.name) ? f.slug : slugify(n) }));
                  }}
                  placeholder="Acme Corp"
                  data-testid="wiz-name"
                  className="mt-1.5 h-10 glass-subtle border-[var(--border)]"
                />
              </div>
              <div>
                <Label className="text-xs">Slug <span className="text-[var(--text-3)] font-mono">(url-safe)</span></Label>
                <Input
                  value={form.slug}
                  onChange={(e) => setForm((f) => ({ ...f, slug: slugify(e.target.value) }))}
                  placeholder="acme-corp"
                  data-testid="wiz-slug"
                  className="mt-1.5 h-10 glass-subtle border-[var(--border)] font-mono text-xs"
                />
              </div>
            </motion.div>
          )}

          {step === 1 && (
            <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
              <div>
                <Label className="text-xs">Industry</Label>
                <Select value={form.industry} onValueChange={(v) => setForm((f) => ({ ...f, industry: v }))}>
                  <SelectTrigger data-testid="wiz-industry" className="mt-1.5 glass-subtle border-[var(--border)]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {industries.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Operating regions</Label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {regionsCatalog.map((r) => {
                    const on = form.regions.includes(r.id);
                    return (
                      <label key={r.id} data-testid={`wiz-region-${r.id}`} className={`flex items-start gap-2 p-2 rounded-md border cursor-pointer transition ${on ? "bg-[var(--surface-strong)] border-[var(--accent)]" : "bg-[var(--surface)] border-[var(--border)]"}`}>
                        <Checkbox
                          checked={on}
                          onCheckedChange={(v) => {
                            const next = v ? [...form.regions, r.id] : form.regions.filter((x) => x !== r.id);
                            setForm((f) => ({ ...f, regions: next }));
                          }}
                          className="mt-0.5"
                        />
                        <div className="text-xs flex-1">
                          <div className="font-medium">{r.name}</div>
                          <div className="text-[10px] text-[var(--text-3)] font-mono mt-0.5">{(r.compliance || []).join(", ").toUpperCase()}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-3">
              <div>
                <Label className="text-xs">Admin full name</Label>
                <Input value={form.admin_name} onChange={(e) => setForm((f) => ({ ...f, admin_name: e.target.value }))} placeholder="Alice CEO" data-testid="wiz-admin-name" className="mt-1.5 h-10 glass-subtle border-[var(--border)]" />
              </div>
              <div>
                <Label className="text-xs">Admin email</Label>
                <Input value={form.admin_email} type="email" onChange={(e) => setForm((f) => ({ ...f, admin_email: e.target.value }))} placeholder="alice@acme.com" data-testid="wiz-admin-email" className="mt-1.5 h-10 glass-subtle border-[var(--border)]" />
              </div>
              <div>
                <Label className="text-xs flex items-center justify-between">
                  Temporary password
                  <button type="button" onClick={() => setForm((f) => ({ ...f, admin_password: randomPassword() }))} data-testid="wiz-regen-pw" className="text-[10px] text-[var(--accent)] hover:underline">Regenerate</button>
                </Label>
                <Input value={form.admin_password} onChange={(e) => setForm((f) => ({ ...f, admin_password: e.target.value }))} data-testid="wiz-admin-password" className="mt-1.5 h-10 glass-subtle border-[var(--border)] font-mono" />
                <div className="text-[10px] text-[var(--text-3)] mt-1">The admin will be prompted to change this on first login.</div>
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div key="s3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-3">
              <div className="text-xs text-[var(--text-3)] mb-1">Optionally seed with a pre-built industry pack — boilerplates, presets, workflows + mappings in one shot. You can skip and add packs later.</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, starter_pack_id: "" }))}
                  data-testid="wiz-pack-none"
                  className={`p-3 text-start rounded-lg border transition ${!form.starter_pack_id ? "bg-[var(--surface-strong)] border-[var(--accent)]" : "bg-[var(--surface)] border-[var(--border)]"}`}
                >
                  <div className="text-xs font-semibold">No starter pack</div>
                  <div className="text-[10px] text-[var(--text-3)]">Empty tenant — configure manually</div>
                </button>
                {packs.map((p) => {
                  const on = form.starter_pack_id === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, starter_pack_id: p.id }))}
                      data-testid={`wiz-pack-${p.id}`}
                      className={`p-3 text-start rounded-lg border transition ${on ? "bg-[var(--surface-strong)] border-[var(--accent)]" : "bg-[var(--surface)] border-[var(--border)]"}`}
                    >
                      <div className="text-xs font-semibold">{p.name}</div>
                      <div className="text-[10px] text-[var(--text-3)] line-clamp-2 mt-0.5">{p.description}</div>
                      <div className="text-[10px] text-[var(--text-3)] font-mono mt-1.5">
                        {p.counts.boilerplates} bp · {p.counts.presets} presets · {p.counts.workflows} wf · {p.counts.mappings} map
                      </div>
                    </button>
                  );
                })}
              </div>
              {form.starter_pack_id && (
                <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-[var(--text-3)] border border-dashed border-[var(--border)] rounded-md px-3 py-2" data-testid="wiz-pack-slices">
                  <span className="uppercase tracking-wider font-medium me-1">Include:</span>
                  {["presets", "workflows", "boilerplates", "mappings"].map((k) => {
                    const on = form.starter_pack_slices?.includes(k);
                    return (
                      <label key={k} className="inline-flex items-center gap-1 cursor-pointer select-none capitalize">
                        <input
                          type="checkbox"
                          checked={!!on}
                          onChange={(e) =>
                            setForm((f) => {
                              const cur = new Set(f.starter_pack_slices || []);
                              if (e.target.checked) cur.add(k); else cur.delete(k);
                              return { ...f, starter_pack_slices: Array.from(cur) };
                            })
                          }
                          className="accent-[var(--accent)]"
                          data-testid={`wiz-pack-slice-${k}`}
                        />
                        {k}
                      </label>
                    );
                  })}
                </div>
              )}
            </motion.div>
          )}

          {step === 4 && (
            <motion.div key="s4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-3">
              <GlassCard className="p-4 space-y-2 text-xs">
                <Row k="Tenant"         v={`${form.name} (${form.slug})`} />
                <Row k="Industry"       v={form.industry} />
                <Row k="Regions"        v={form.regions.join(", ")} />
                <Row k="First admin"    v={`${form.admin_name || "(no name)"} · ${form.admin_email}`} />
                <Row k="Temp password"  v={<code className="font-mono">{form.admin_password}</code>} />
                <Row k="Starter pack"   v={form.starter_pack_id ? (packs.find((p) => p.id === form.starter_pack_id)?.name) : "None"} />
              </GlassCard>
            </motion.div>
          )}

          {step === 5 && created && (
            <motion.div key="s5" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="space-y-3 text-center py-2">
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-400 flex items-center justify-center">
                <Check size={26} weight="bold" />
              </div>
              <div className="text-base font-semibold" style={{ fontFamily: "var(--font-heading)" }}>Tenant ready</div>
              <div className="text-xs text-[var(--text-2)]">Copy the credentials — the admin needs them to sign in.</div>
              <GlassCard className="p-3 text-start text-xs font-mono">
                <div><span className="text-[var(--text-3)]">email:</span> {form.admin_email}</div>
                <div><span className="text-[var(--text-3)]">password:</span> {form.admin_password}</div>
              </GlassCard>
              <div className="flex justify-center gap-2">
                <Button variant="outline" onClick={copyCreds} data-testid="wiz-copy-creds" className="gap-2"><Copy size={13} /> Copy</Button>
                <Button onClick={() => onOpenChange(false)} data-testid="wiz-finish" className="gap-2"><Check size={13} /> Done</Button>
              </div>
              {created.pack_applied?.created && (
                <div className="text-[11px] text-[var(--text-3)]">
                  Starter pack "{created.pack_applied.name}" applied — {created.pack_applied.created.boilerplates.length} boilerplates, {created.pack_applied.created.presets.length} presets, {created.pack_applied.created.workflows.length} workflows, {created.pack_applied.created.mappings.length} mappings.
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {step < 5 && (
          <DialogFooter className="gap-2 mt-3">
            {step > 0 && <Button variant="ghost" onClick={() => setStep(step - 1)} data-testid="wiz-back" className="gap-2"><ArrowLeft size={13} /> Back</Button>}
            <div className="flex-1" />
            {step < 4 && <Button onClick={() => setStep(step + 1)} disabled={!canNext()} data-testid="wiz-next" className="gap-2">Next <ArrowRight size={13} /></Button>}
            {step === 4 && <Button onClick={submit} disabled={submitting} data-testid="wiz-submit" className="gap-2"><Check size={13} /> {submitting ? "Provisioning…" : "Create tenant"}</Button>}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-[var(--border)] last:border-0 py-1.5">
      <span className="text-[var(--text-3)]">{k}</span>
      <span className="font-medium truncate">{v}</span>
    </div>
  );
}
